import { useState } from 'react';
import {
  Card, CardContent, Typography, Button, Box, Chip, Paper,
  Accordion, AccordionSummary, AccordionDetails, List, ListItem,
  ListItemIcon, ListItemText, Link,
  CircularProgress, Alert
} from '@mui/material';
import MathMarkdown from '../components/MathMarkdown';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ArticleIcon from '@mui/icons-material/Article';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CalculateIcon from '@mui/icons-material/Calculate';
import SchoolIcon from '@mui/icons-material/School';
import DownloadIcon from '@mui/icons-material/Download';
import { useQuery } from '@tanstack/react-query';
import { useSession } from '../context/SessionContext';
import { feedbackApi } from '../utils/api';
import { SessionSummary, SkillMastery, Resource } from '../types';

const masteryColors = {
  mastered: 'success',
  developing: 'warning',
  not_yet_mastered: 'warning',
} as const;

const masteryLabels = {
  mastered: 'Mastered',
  developing: 'Review Recommended',
  not_yet_mastered: 'Review Recommended',
};

const resourceIcons: Record<string, React.ReactNode> = {
  Video: <PlayCircleOutlineIcon />,
  Article: <ArticleIcon />,
  Practice: <CalculateIcon />,
  Worksheet: <AssignmentIcon />,
};

export default function FeedbackPage() {
  const { session, updateStatus } = useSession();
  const courseLevel = session?.assessment_course_level?.trim() || 'Precalculus I';
  const { data: summary, isLoading: loading } = useQuery<SessionSummary>({
    queryKey: ['feedback', 'summary'],
    queryFn: () => feedbackApi.getSummary(),
  });
  const [showAllSkills, setShowAllSkills] = useState(false);

  const handleContinue = async () => {
    await updateStatus('post_survey');
  };

  const handleDownloadPDF = () => {
    const apiUrl = import.meta.env.VITE_API_URL || '/api';
    window.open(`${apiUrl}/feedback/pdf`, '_blank');
  };

  if (loading || !summary) {
    return (
      <Card>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress sx={{ mb: 3 }} />
          <Typography>Generating your personalized feedback...</Typography>
        </CardContent>
      </Card>
    );
  }

  const reviewSkills = summary.review_skills_json || [];
  const displaySkills = showAllSkills ? reviewSkills : reviewSkills.slice(0, 5);

  return (
    <Box>
      {/* Overview Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
            <Typography variant="h4" component="h1" color="primary">
              Your Feedback
            </Typography>
          </Box>
          {/* AI Feedback */}
          <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
            {summary.ai_feedback_text}
          </Typography>
        </CardContent>
      </Card>

      {/* Skills to Review */}
      {reviewSkills.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              Skills to Review
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {`The skills below are ordered by priority based on how important each is for ${courseLevel} and the evidence from your responses. Start with the first skills listed, then work down as time allows.`}
            </Typography>

            <List>
              {displaySkills.map((skill: SkillMastery, index: number) => (
                <Accordion key={skill.skill_id} defaultExpanded={index === 0}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box display="flex" alignItems="center" gap={2} width="100%">
                      {skill.mastery_level === 'developing' ? (
                        <TrendingUpIcon color="warning" />
                      ) : (
                        <TrendingUpIcon color="warning" />
                      )}
                      <Box flex={1}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{skill.skill_name}</Typography>
                      </Box>
                      <Chip
                        label={masteryLabels[skill.mastery_level]}
                        color={masteryColors[skill.mastery_level]}
                        size="small"
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {/* Resources for this skill */}
                    {skill.resources && skill.resources.length > 0 && (
                      <>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                          Resources To Support This Skill
                        </Typography>
                        <List dense>
                          {skill.resources.slice(0, 3).map((resource: Resource) => (
                            <ListItem key={resource.resource_id} disablePadding>
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                {resourceIcons[resource.resource_type] || <SchoolIcon />}
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Link
                                    href={resource.resource_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {resource.resource_title}
                                  </Link>
                                }
                                secondary={resource.resource_type}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </>
                    )}
                    {/* Questions blamed for this skill */}
                    {skill.questions && skill.questions.filter(q => q.is_blamed).length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                          Examples From Your Responses
                        </Typography>
                        <List dense>
                          {skill.questions.filter(q => q.is_blamed).map((q) => (
                            <Paper key={q.question_id} variant="outlined" sx={{ p: 2, mb: 1.5 }}>
                              <Typography variant="body2" fontWeight={800} gutterBottom>
                                Question {q.question_number}
                              </Typography>
                              <MathMarkdown sx={{ mb: 1, '& p': { margin: 0 } }}>
                                {q.question_text}
                              </MathMarkdown>
                              <Box sx={{ pl: 2, borderLeft: 2, borderColor: 'warning.main' }}>
                                {!q.is_correct && (
                                  <>
                                    <Box sx={{ color: 'text.secondary', typography: 'body2' }}>
                                      <MathMarkdown>
                                        {q.user_answer?.trim()
                                          ? `Your answer: $$${q.user_answer}$$`
                                          : 'Your answer: Skipped'}
                                      </MathMarkdown>
                                    </Box>
                                    <Box sx={{ color: 'success.main', typography: 'body2' }}>
                                      <MathMarkdown>
                                        {`Correct answer: ${q.correct_answer}`}
                                      </MathMarkdown>
                                    </Box>
                                  </>
                                )}
                              </Box>
                            </Paper>
                          ))}
                        </List>
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              ))}
            </List>

            {reviewSkills.length > 5 && (
              <Box textAlign="center" sx={{ mt: 2 }}>
                <Button onClick={() => setShowAllSkills(!showAllSkills)}>
                  {showAllSkills ? 'Show Less' : `Show ${reviewSkills.length - 5} More Skills`}
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      )}
      {/* Reminder Alert to Download PDF */}
      <Box sx={{ mb: 3 }}>
        <Alert
          icon={<DownloadIcon color="warning" />}
          severity="warning"
          sx={{ alignItems: 'center' }}
        >
          Before moving on, consider saving a PDF copy of your feedback so you can easily refer back to it.
        </Alert>
      </Box>
      {/* Continue Button */}
      <Box textAlign="center" display="flex" gap={2} justifyContent="center">
        <Button
          variant="outlined"
          size="large"
          startIcon={<DownloadIcon />}
          onClick={handleDownloadPDF}
          sx={{ px: 4 }}
        >
          Download PDF
        </Button>
        <Button
          variant="contained"
          size="large"
          onClick={handleContinue}
          sx={{ px: 6 }}
        >
          Continue
        </Button>
      </Box>
    </Box>
  );
}
